import { useState } from "react";
import {
  Activity,
  AlertTriangle,
  ClipboardList,
  Clock,
  FileText,
  HeartPulse,
  Plus,
  Thermometer,
} from "lucide-react";
import HealthSafetySchoolsModal from "@/components/HealthSafetySchoolsModal";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import {
  avg,
  buildHealthActionQueue,
  buildHealthClosurePlan,
  deriveTestingTarget,
  fmt,
  getFirstAidChipLabel,
  pct,
} from "@/utils/waterSafetyDerived";
import WaterSafetyGuidanceChip from "@/components/WaterSafetyGuidanceChip";
import HeroStatusChip from "@/components/HeroStatusChip";
import { toTitleCase } from "../utils/toTitleCase";

const buildHealthGaugeStyle = (value) => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--inf-success) 0 ${Math.min(value, 100)}%, rgba(16,185,129,.12) ${Math.min(value, 100)}% 100%)`,
});

const isFacilityOk = (value) => {
  const v = String(value ?? "").toLowerCase().trim();
  return v === "yes" || v === "y" || v === "1" || v === "available" || v === "all";
};

const getRiskAction = (riskCount) => {
  if (riskCount >= 5) return { label: "Critical", tone: "red" };
  if (riskCount >= 4) return { label: "High", tone: "amber" };
  return { label: "Watch", tone: "" };
};

const StatusCell = ({ value }) => (
  <span className={`inf-status-dot-cell ${isFacilityOk(value) ? "ok" : ""}`}>
    {isFacilityOk(value) ? "✓" : "×"}
  </span>
);

const KPI_ICONS = {
  "Medical Check-up": Activity,
  "Health Records": FileText,
  "First Aid": Plus,
  "Life-Saving Equip": Clock,
  "Thermal Screening": Thermometer,
};

const KPI_ICON_TONES = {
  "Medical Check-up": "",
  "Health Records": "purple",
  "First Aid": "green",
  "Life-Saving Equip": "orange",
  "Thermal Screening": "cyan",
};

const PROGRESS_TONES = {
  "Medical Check-up": "",
  "Health Records": "purple",
  "First Aid": "green",
  "Life-Saving Equip": "orange",
  "Thermal Screening": "cyan",
};

const HEALTH_KPI_MODAL_KEYS = {
  "Medical Check-up": "medical_checkup",
  "Health Records": "health_record",
  "First Aid": "first_aid",
  "Life-Saving Equip": "life_saving",
  "Thermal Screening": "thermal_screening",
};

const HEALTH_KPI_COVERAGE = {
  medical_checkup: (overview) => overview?.medical_checkup_coverage,
  health_record: (overview) => overview?.health_record_coverage,
  first_aid: (overview) => overview?.first_aid_coverage,
  life_saving: (overview) => overview?.life_saving_coverage,
  thermal_screening: (overview) => overview?.thermal_coverage,
};

const WaterSafetyHealthTab = ({
  overview,
  blockData,
  healthMetrics,
  highRiskSchools,
  guidance,
  guidanceProvider = "local",
  guidanceLoading = false,
  scopeLabel = "Pune District",
}) => {
  const [healthMetricModal, setHealthMetricModal] = useState(null);
  if (!overview) {
    return (
      <div className="inf-empty-card">
        <HeartPulse className="mx-auto mb-3 opacity-40" size={40} />
        <p>No health & safety data available for the selected scope.</p>
      </div>
    );
  }

  const metrics = Array.isArray(healthMetrics) ? healthMetrics : [];
  const totalSchools = metrics[0]?.total ?? overview.total_schools ?? 0;

  const medicalPct = overview.medical_checkup_coverage ?? 0;
  const healthRecordPct = overview.health_record_coverage ?? 0;
  const firstAidPct = overview.first_aid_coverage ?? 0;
  const lifeSavingPct = overview.life_saving_coverage ?? 0;
  const thermalPct = overview.thermal_coverage ?? 0;

  const readinessScore = avg(medicalPct, healthRecordPct, firstAidPct, lifeSavingPct, thermalPct);
  const thermalGap = Math.max(0, totalSchools - (overview.thermal_yes ?? 0));
  const lifeSavingGap = Math.max(0, totalSchools - (overview.life_saving_yes ?? 0));
  const healthRecordGap = Math.max(0, totalSchools - (overview.health_record_yes ?? 0));
  const healthReadinessTarget = avg(medicalPct, healthRecordPct, firstAidPct, lifeSavingPct, thermalPct);
  const actionGapCount = [thermalGap, lifeSavingGap, healthRecordGap].filter((g) => g > totalSchools * 0.05).length;
  const localActionQueue = buildHealthActionQueue(overview, highRiskSchools);
  const actionQueue = (guidance?.action_queue?.length ? guidance.action_queue : localActionQueue).map((action, index) => ({
    title: action.title,
    description: action.description,
    priority: action.priority ?? localActionQueue[index]?.priority,
    deadline: action.deadline_days ?? action.deadline ?? localActionQueue[index]?.deadline ?? 30,
  }));
  const closurePlan =
    guidance?.closure_plan?.length > 0 ? guidance.closure_plan : buildHealthClosurePlan(overview, highRiskSchools);
  const thermalTarget = deriveTestingTarget(thermalPct, totalSchools, overview.thermal_yes);
  const heroSummary =
    guidance?.hero_summary ??
    `Average health readiness is ${pct(healthReadinessTarget)} across ${fmt(totalSchools)} schools. First aid is ${pct(firstAidPct)} and medical check-ups are ${pct(medicalPct)}, while ${fmt(thermalGap)} schools lack thermal screening and ${fmt(lifeSavingGap)} lack life-saving equipment.`;
  const primaryAction =
    guidance?.primary_action ??
    `Prioritize ${(highRiskSchools ?? []).filter((s) => (s.risk_count ?? 0) >= 5).length} risk-score-5 schools and close ${fmt(thermalGap)} thermal and ${fmt(lifeSavingGap)} life-saving equipment gaps within ${actionQueue[0]?.deadline ?? 30} days.`;
  const reviewRecommendation =
    guidance?.review_recommendation ??
    `Run weekly review until thermal screening reaches ${pct(thermalTarget)} (from ${pct(thermalPct)}) and all ${(highRiskSchools ?? []).filter((s) => (s.risk_count ?? 0) >= 5).length} risk-score-5 schools are verified.`;

  const preparednessBlocks = [...(blockData ?? [])]
    .sort((a, b) => (b.health_readiness_score ?? 0) - (a.health_readiness_score ?? 0))
    .slice(0, 8);

  const riskSchools = (highRiskSchools ?? []).slice(0, 8);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openHealthMetric = (metricName) => {
    const modalKey = HEALTH_KPI_MODAL_KEYS[metricName];
    if (!modalKey) return;
    const metricRow = metrics.find((m) => m.name === metricName);
    const missing = Math.max(0, (metricRow?.total ?? totalSchools) - (metricRow?.count ?? 0));
    if (missing <= 0) return;
    setHealthMetricModal(modalKey);
  };

  const firstAidChip = getFirstAidChipLabel(firstAidPct);
  const firstAidGap = Math.max(0, totalSchools - (overview.first_aid_yes ?? 0));

  const openPrimaryHealthGap = () => {
    if (thermalGap > totalSchools * 0.05) openHealthMetric("Thermal Screening");
    else if (lifeSavingGap > totalSchools * 0.05) openHealthMetric("Life-Saving Equip");
    else if (healthRecordGap > totalSchools * 0.05) openHealthMetric("Health Records");
    else scrollTo("inf-health-actions");
  };

  return (
    <div className="inf-water-tab inf-health-tab" data-testid="tab-health-content" id="inf-health-overview">
      <section className="inf-hero-card inf-hero-card-health">
        <div className="inf-hero-layout">
          <div>
            <div className="inf-health-main">
              <div
                className="inf-health-gauge"
                style={buildHealthGaugeStyle(readinessScore)}
                aria-label={`Health safety readiness score ${readinessScore.toFixed(1)} percent`}
              >
                <div>
                  <strong>{readinessScore.toFixed(1)}%</strong>
                  <span>Readiness</span>
                </div>
              </div>
              <div className="inf-hero-copy">
                <h2>Health & Emergency Readiness Command Centre</h2>
                <p>{heroSummary}</p>
                <div className="inf-status-row">
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant={firstAidChip.tone}
                    onClick={firstAidGap > 0 ? () => openHealthMetric("First Aid") : undefined}
                    ariaLabel={
                      firstAidGap > 0 ? `${firstAidChip.label} — view schools missing first aid` : undefined
                    }
                  >
                    {firstAidChip.label}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant="amber"
                    onClick={actionGapCount > 0 ? openPrimaryHealthGap : undefined}
                    ariaLabel={
                      actionGapCount > 0
                        ? `${actionGapCount} gaps need action — view affected schools`
                        : undefined
                    }
                  >
                    {actionGapCount} gap{actionGapCount === 1 ? "" : "s"} need action
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="inf-status-chip" variant="blue" showChevron={false}>
                    {fmt(totalSchools)} schools monitored
                  </HeroStatusChip>
                  <WaterSafetyGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                </div>
              </div>
            </div>
            <div className="inf-summary-note">
              <div className="inf-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary officer action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="inf-metric-grid" aria-label="Executive KPIs">
            <article className="inf-metric-card">
              <small>Medical check-up</small>
              <strong>{pct(medicalPct)}</strong>
              <span>{fmt(overview.medical_checkup_yes)} schools completed</span>
            </article>
            <article className="inf-metric-card inf-metric-purple">
              <small>Health records</small>
              <strong>{pct(healthRecordPct)}</strong>
              <span>{fmt(overview.health_record_yes)} schools digitized</span>
            </article>
            <article className="inf-metric-card">
              <small>First aid readiness</small>
              <strong>{pct(firstAidPct)}</strong>
              <span>{fmt(overview.first_aid_yes)} schools equipped</span>
            </article>
            <article className="inf-metric-card warn">
              <small>Thermal screening</small>
              <strong>{pct(thermalPct)}</strong>
              <span>Largest readiness gap</span>
            </article>
          </div>
        </div>
      </section>

      <section className="inf-kpi-strip" aria-label="Health and safety KPI cards">
        {metrics.map((metric) => {
          const Icon = KPI_ICONS[metric.name] ?? HeartPulse;
          const tone = KPI_ICON_TONES[metric.name] ?? "";
          const modalKey = HEALTH_KPI_MODAL_KEYS[metric.name];
          const missing = Math.max(0, (metric.total ?? totalSchools) - (metric.count ?? 0));
          const clickable = Boolean(modalKey && missing > 0);
          return (
            <article
              className={`inf-kpi-card${clickable ? " clickable" : ""}`}
              key={metric.name}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={clickable ? `${metric.name}: view schools missing coverage` : undefined}
              onClick={clickable ? () => openHealthMetric(metric.name) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openHealthMetric(metric.name);
                      }
                    }
                  : undefined
              }
              title={clickable ? `View ${fmt(missing)} schools missing ${metric.name.toLowerCase()}` : undefined}
            >
              <div>
                <small>{metric.name}</small>
                <strong>{pct(metric.percentage)}</strong>
                <span>{fmt(metric.count)} schools</span>
              </div>
              <div className={`inf-mini-icon ${tone}`}>
                <Icon size={20} />
              </div>
            </article>
          );
        })}
      </section>

      <div className="inf-section-title" id="inf-health-diagnostics">
        <div>
          <h2>Readiness Diagnostics</h2>
          <p>
            Instead of only showing raw bars, the redesigned view separates readiness, emergency
            preparedness and gaps so officers can instantly see what is strong and what requires action.
          </p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-health-actions")}>
          View action queue →
        </button>
      </div>

      <section className="inf-diagnostic-grid">
        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Health & Safety Compliance Stack</h3>
              <p>Facility-wise readiness with missing-school counts converted into actionable gaps.</p>
            </div>
            <span className="inf-pill">District view</span>
          </div>
          <div className="inf-readiness-list">
            {metrics.map((metric) => (
              <div className="inf-readiness-row" key={metric.name}>
                <div className="inf-readiness-top">
                  <span>
                    <b>{metric.name}</b> · {fmt(metric.count)} schools{" "}
                    {metric.name === "Medical Check-up"
                      ? "completed"
                      : metric.name === "Health Records"
                        ? "digitized"
                        : metric.name === "First Aid"
                          ? "equipped"
                          : "available"}
                  </span>
                  <b>{pct(metric.percentage)}</b>
                </div>
                <div className="inf-progress-track">
                  <div
                    className={`inf-progress-fill ${PROGRESS_TONES[metric.name] ?? ""}`}
                    style={{ width: `${Math.min(metric.percentage, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Block-Wise Emergency Preparedness</h3>
              <p>Top blocks are near readiness, but each still needs verification for equipment and screening gaps.</p>
            </div>
            <span className="inf-pill">Preparedness</span>
          </div>
          <div className="inf-emergency-bars" aria-label="Block-wise emergency preparedness bar chart">
            {preparednessBlocks.map((block) => {
              const score = block.health_readiness_score ?? 0;
              return (
                <div className="inf-emergency-bar-row" key={block.block_name}>
                  <div className="inf-emergency-bar-label">{block.block_name}</div>
                  <div className="inf-emergency-bar-track">
                    <div className="inf-emergency-bar-fill" style={{ width: `${Math.min(score, 100)}%` }} />
                  </div>
                  <div className="inf-emergency-bar-value">{score.toFixed(1)}</div>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className="inf-risk-grid" aria-label="Gap summary">
        <article className="inf-risk-card">
          <h3>Thermal Screening Gap</h3>
          <strong>{fmt(thermalGap)}</strong>
          <span>schools need screening facility or updated availability confirmation.</span>
        </article>
        <article className="inf-risk-card orange">
          <h3>Life-Saving Equipment Gap</h3>
          <strong>{fmt(lifeSavingGap)}</strong>
          <span>schools need emergency equipment or verification of working condition.</span>
        </article>
        <article className="inf-risk-card purple">
          <h3>Health Records Gap</h3>
          <strong>{fmt(healthRecordGap)}</strong>
          <span>schools need health records digitization and verification.</span>
        </article>
        <article className="inf-risk-card green">
          <h3>First Aid Coverage</h3>
          <strong>{pct(firstAidPct)}</strong>
          <span>strongest domain; maintain refill and expiry tracking.</span>
        </article>
      </section>

      <div className="inf-section-title" id="inf-health-actions">
        <div>
          <h2>High-Risk Schools and Officer Action Queue</h2>
          <p>
            The old table is converted into a focused closure queue with status signals, risk scoring, and
            recommended owner actions.
          </p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-health-overview")}>
          Back to top ↑
        </button>
      </div>

      <section className="inf-two-column">
        <article className="inf-panel inf-table-card">
          <div className="inf-table-head">
            <div>
              <h3>High-Risk Schools</h3>
              <p>Schools missing three or more critical health and safety facilities.</p>
            </div>
            <span className="inf-pill">Immediate review</span>
          </div>
          <div className="inf-table-wrap">
            <table className="inf-data-table inf-health-risk-table" aria-label="High-risk school action table">
              <thead>
                <tr>
                  <th>School Name</th>
                  <th>Block</th>
                  <th>Risk</th>
                  <th>Tap Water</th>
                  <th>Purification</th>
                  <th>Testing</th>
                  <th>Kitchen Shed</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {riskSchools.map((school) => {
                  const action = getRiskAction(school.risk_count ?? 0);
                  return (
                    <tr key={school.udise_code ?? school.school_name}>
                      <td className="inf-school-name">
                        <SchoolLink udiseCode={school.udise_code}>{school.school_name}</SchoolLink>
                      </td>
                      <td>
                        <BlockLink blockCode={school.block_code}>{school.block_name}</BlockLink>
                      </td>
                      <td>
                        <span className="inf-risk-score">{school.risk_count}</span>
                      </td>
                      <td>
                        <StatusCell value={school.tap_water} />
                      </td>
                      <td>
                        <StatusCell value={school.purification} />
                      </td>
                      <td>
                        <StatusCell value={school.testing} />
                      </td>
                      <td>
                        <StatusCell value={school.kitchen_shed} />
                      </td>
                      <td>
                        <span className={`inf-tag ${action.tone}`}>{action.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </article>

        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Prioritized interventions for district review and block-level closure.</p>
            </div>
            <span className="inf-pill">Officer actions</span>
          </div>
          <div className="inf-action-card">
            {actionQueue.map((action, index) => {
              const icons = [AlertTriangle, Clock, ClipboardList, Thermometer];
              const tones = ["red", "orange", "purple", "cyan"];
              const priorities = ["high", "amber", "blue", "low"];
              const Icon = icons[index] ?? AlertTriangle;
              return (
                <div className="inf-action-item" key={action.title}>
                  <div className={`inf-icon-box ${tones[index] ?? "red"}`}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.description}</p>
                  </div>
                  <span className={`inf-priority ${priorities[index] ?? "low"}`}>P{index + 1}</span>
                </div>
              );
            })}
            <div className="inf-action-footer">
              <b>Next review recommendation</b>
              <span>{reviewRecommendation}</span>
            </div>
          </div>
        </article>
      </section>

      <div className="inf-section-title">
        <div>
          <h2>30-Day Closure Plan</h2>
          <p>
            A compact operating plan to move from reporting to field action, evidence collection and
            district-level closure tracking.
          </p>
        </div>
      </div>

      <section className="inf-plan-grid">
        {closurePlan.map((step) => (
          <article className="inf-plan-step-card" key={step.step}>
            <div className="inf-plan-step">{step.step}</div>
            <h3>{toTitleCase(step.title)}</h3>
            <p>{step.description}</p>
          </article>
        ))}
      </section>

      <HealthSafetySchoolsModal
        open={Boolean(healthMetricModal)}
        metric={healthMetricModal}
        onClose={() => setHealthMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          healthMetricModal && HEALTH_KPI_COVERAGE[healthMetricModal]
            ? HEALTH_KPI_COVERAGE[healthMetricModal](overview)
            : null
        }
      />
    </div>
  );
};

export default WaterSafetyHealthTab;
